import React, { useCallback, useEffect, useState } from "react";
import type { User } from "oidc-client-ts";
import {
  userManager,
  login as oidcLogin,
  logout as oidcLogout,
  getAccessToken,
  consumeLoggedOutFlag,
} from "./oidc";
import { AuthContext } from "./useAuth";
import type { AuthContextValue, AuthStatus } from "./useAuth";

interface Props {
  children: React.ReactNode;
}

export function AuthProvider({ children }: Props) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>("initializing");
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    void (async () => {
      // Retour d'un logout volontaire → ne PAS tenter signinSilent (le cookie SSO
      // Keycloak peut survivre et reconnecterait aussitôt). Rester déconnecté.
      const justLoggedOut = consumeLoggedOutFlag();
      if (justLoggedOut) {
        // logout() n'a volontairement pas fait removeUser() (évite la course
        // auto-login). On nettoie le user local maintenant qu'on est revenu déco.
        await userManager.removeUser();
        setStatus("unauthenticated");
        return;
      }
      const loadedUser = await userManager.getUser();
      if (loadedUser && !loadedUser.expired) {
        setUser(loadedUser);
        setStatus("authenticated");
        return;
      }
      try {
        const renewed = await userManager.signinSilent();
        if (renewed && !renewed.expired) {
          setUser(renewed);
          setStatus("authenticated");
          return;
        }
      } catch {
        // Cookie SSO absent/expiré → vraie déconnexion.
      }
      setStatus("unauthenticated");
    })();

    const onUserLoaded = (loadedUser: User) => {
      setUser(loadedUser);
      setStatus("authenticated");
      setError(null);
    };

    const onUserUnloaded = () => {
      setUser(null);
      setStatus("unauthenticated");
    };

    const onAccessTokenExpiring = () => {
      setStatus("renewing");
    };

    const onSilentRenewError = (err: Error) => {
      setError(err);
      setStatus("error");
    };

    const onUserSignedOut = () => {
      setUser(null);
      setStatus("unauthenticated");
    };

    userManager.events.addUserLoaded(onUserLoaded);
    userManager.events.addUserUnloaded(onUserUnloaded);
    userManager.events.addAccessTokenExpiring(onAccessTokenExpiring);
    userManager.events.addSilentRenewError(onSilentRenewError);
    userManager.events.addUserSignedOut(onUserSignedOut);

    return () => {
      userManager.events.removeUserLoaded(onUserLoaded);
      userManager.events.removeUserUnloaded(onUserUnloaded);
      userManager.events.removeAccessTokenExpiring(onAccessTokenExpiring);
      userManager.events.removeSilentRenewError(onSilentRenewError);
      userManager.events.removeUserSignedOut(onUserSignedOut);
    };
  }, []);

  const login = useCallback(async (returnUrl?: string): Promise<void> => {
    setStatus("initializing");
    await oidcLogin(returnUrl);
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    setStatus("initializing");
    try {
      await oidcLogout();
    } catch {
      window.location.replace("/");
    }
  }, []);

  const value: AuthContextValue = {
    user,
    status,
    error,
    login,
    logout,
    getAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
